import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_CHAT } from "../../graphql/queries";
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  ListGroup,
  Image,
} from "react-bootstrap";

const ChatDetail = () => {
  const { id } = useParams();
  const { loading, error, data } = useQuery(GET_CHAT, {
    variables: { id },
    pollInterval: 15000, // Poll for new messages every 15 seconds
  });

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    // Handle different error types
    if (error.message.includes("Authentication required")) {
      return (
        <Container className="mt-4">
          <Alert variant="warning">
            <Alert.Heading>Authentication Required</Alert.Heading>
            <p>Please log in to view chat details.</p>
          </Alert>
        </Container>
      );
    }

    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error.message}</p>
        </Alert>
      </Container>
    );
  }

  const chat = data?.chat;
  if (!chat) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">
          <Alert.Heading>Chat Not Found</Alert.Heading>
          <p>The requested chat could not be found.</p>
        </Alert>
      </Container>
    );
  }

  // Format timestamp to readable date
  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
  };

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          <h2 className="mb-4">{chat.name}</h2>
          {chat.description && <p className="lead">{chat.description}</p>}
          <div className="d-flex mb-3">
            <div className="me-4">
              <strong>Members:</strong> {chat.memberCount}
            </div>
            <div className="me-4">
              <strong>Messages:</strong> {chat.messageCount}
            </div>
            <div>
              <strong>Created:</strong> {formatDate(chat.createdAt)}
            </div>
          </div>
        </Col>
      </Row>

      {/* Group Image */}
      {chat.imageUrl && (
        <Row className="mb-4">
          <Col xs={12} md={4}>
            <Image
              src={chat.imageUrl}
              alt={chat.name}
              fluid
              className="rounded"
            />
          </Col>
        </Row>
      )}

      <h3 className="mb-3">Recent Messages</h3>
      <Card>
        <ListGroup variant="flush">
          {chat.messages && chat.messages.length > 0 ? (
            chat.messages.map((message) => (
              <ListGroup.Item
                key={message.id}
                className={message.system ? "bg-light" : ""}
              >
                <div className="d-flex">
                  {message.avatarUrl && (
                    <div className="me-3">
                      <Image
                        src={message.avatarUrl}
                        roundedCircle
                        width={40}
                        height={40}
                      />
                    </div>
                  )}
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between">
                      <strong>{message.name}</strong>
                      <small className="text-muted">
                        {formatDate(message.createdAt)}
                      </small>
                    </div>
                    <div>{message.text}</div>
                  </div>
                </div>
              </ListGroup.Item>
            ))
          ) : (
            <ListGroup.Item>No messages available</ListGroup.Item>
          )}
        </ListGroup>
      </Card>

      <h3 className="mt-4 mb-3">Members</h3>
      <Row xs={2} md={3} lg={4} className="g-3 mb-4">
        {chat.members && chat.members.length > 0 ? (
          chat.members.map((member) => (
            <Col key={member.id}>
              <Card className="h-100">
                {member.imageUrl && (
                  <Card.Img
                    variant="top"
                    src={member.imageUrl}
                    alt={member.name}
                    style={{ height: "100px", objectFit: "cover" }}
                  />
                )}
                <Card.Body className="p-2">
                  <Card.Title className="fs-6">{member.name}</Card.Title>
                  {member.nickname && member.nickname !== member.name && (
                    <Card.Text className="text-muted small mb-0">
                      {member.nickname}
                    </Card.Text>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))
        ) : (
          <Col>
            <Alert variant="info">No member information available</Alert>
          </Col>
        )}
      </Row>
    </Container>
  );
};

export default ChatDetail;
